import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import user from "../../assets/images/user.png";
import {
  GoogleMap,
  LoadScriptNext,
  Marker,
  Polyline,
  DirectionsService,
} from "@react-google-maps/api";
import { fynitoApi } from "../Services/axios";
import toastify from "../../helper/toastify";
import OrderProgressBar from "../Order/OrderProgressbar";
import MobileBottomBar from "../Layout/MobileBottomBar";

const GOOGLE_MAPS_API_KEY = "AIzaSyALUgxLSFhopWw3n3ahz_exNBuP6a1dMzA";
const libraries = ["places", "directions"];

const containerStyle = {
  width: "100%",
  height: "100%",
  minHeight: "500px",
};

const defaultCenter = {
  lat: 27.6242258,
  lng: 75.1664756,
};

const TrackOrder = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [mapPoints, setMapPoints] = useState({
    start: null,
    end: null,
  });
  const [orderDetails, setOrderDetails] = useState({});
  const [restDetails, setRestDetails] = useState({});
  const [markers, setMarkers] = useState([]);
  const [polylinePath, setPolylinePath] = useState([]);
  const [distance, setDistance] = useState({ km: 0, time: "" });
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [zoom, setZoom] = useState(13);
  const [directionsResponse, setDirectionsResponse] = useState(null);
  const mapRef = useRef(null);
  const directionsServiceRef = useRef(null);
  const updateIntervalRef = useRef(null);
  const directionsRequestedKeyRef = useRef(null);
  const pendingDirectionsKeyRef = useRef(null);

  // Update markers based on order status
  const updateMarkers = useCallback((data) => {
    const newMarkers = [];
    const status = data?.status;
    const deliveryPartner = data?.delivery_partner;
    const restaurant = data?.restaurant;
    const deliveryAddress = data?.delivery_address_detail;

    // Check if we should show markers based on status
    const shouldShowMarkers = 
      status === "Out For Delivery" ||
      status === "Picked Up" ||
      status === "Ready" ||
      status === "Processing" ||
      status === "Delivered";

    if (deliveryPartner && deliveryPartner.name) {
      // Delivery boy is assigned
      const isPicked = deliveryPartner.pickedor_not === 1;

      if (deliveryAddress?.lat && deliveryAddress?.long && shouldShowMarkers) {
        if (!isPicked) {
          // Show restaurant marker
          if (restaurant?.latitude && restaurant?.longitude) {
            newMarkers.push({
              id: "restLoc",
              position: {
                lat: parseFloat(restaurant.latitude),
                lng: parseFloat(restaurant.longitude),
              },
              title: restaurant.restaurant_name || "Restaurant",
              snippet: restaurant.full_address || "",
              icon: "https://maps.google.com/mapfiles/ms/icons/red-dot.png",
            });
          }
        } else {
          // Show user location marker
          newMarkers.push({
            id: "userLoc",
            position: {
              lat: parseFloat(deliveryAddress.lat),
              lng: parseFloat(deliveryAddress.long),
            },
            title: deliveryAddress.name || "Delivery Address",
            snippet: `${deliveryAddress.flat ? deliveryAddress.flat + ", " : ""}${deliveryAddress.area ? deliveryAddress.area + ", " : ""}${deliveryAddress.address || ""}`,
            icon: "https://maps.google.com/mapfiles/ms/icons/blue-dot.png",
          });
        }
      }

      // Show delivery boy marker
      if (deliveryPartner.delivery_boy_lat && deliveryPartner.delivery_boy_long) {
        newMarkers.push({
          id: "boyLoc",
          position: {
            lat: parseFloat(deliveryPartner.delivery_boy_lat),
            lng: parseFloat(deliveryPartner.delivery_boy_long),
          },
          title: deliveryPartner.name || "Delivery Boy",
          snippet: deliveryPartner.phone || "",
          icon: "https://maps.google.com/mapfiles/ms/icons/green-dot.png",
        });
      }
    } else {
      // No delivery boy assigned - show restaurant and user location
      if (deliveryAddress?.lat && deliveryAddress?.long) {
        newMarkers.push({
          id: "userLoc",
          position: {
            lat: parseFloat(deliveryAddress.lat),
            lng: parseFloat(deliveryAddress.long),
          },
          title: deliveryAddress.name || "Delivery Address",
          snippet: `${deliveryAddress.flat ? deliveryAddress.flat + ", " : ""}${deliveryAddress.area ? deliveryAddress.area + ", " : ""}${deliveryAddress.address || ""}`,
          icon: "https://maps.google.com/mapfiles/ms/icons/blue-dot.png",
        });
      }

      if (restaurant?.latitude && restaurant?.longitude) {
        newMarkers.push({
          id: "restLoc",
          position: {
            lat: parseFloat(restaurant.latitude),
            lng: parseFloat(restaurant.longitude),
          },
          title: restaurant.restaurant_name || "Restaurant",
          snippet: restaurant.full_address || "",
          icon: "https://maps.google.com/mapfiles/ms/icons/red-dot.png",
        });
      }
    }

    setMarkers(newMarkers);
    return newMarkers;
  }, []);

  // Calculate distance using Google Distance Matrix API
  const calculateDistance = useCallback(async (origin, destination) => {
    if (!origin || !destination) return;

    try {
      const url = `https://maps.googleapis.com/maps/api/distancematrix/json?destinations=${destination.lat},${destination.lng}&origins=${origin.lat},${origin.lng}&units=metric&key=${GOOGLE_MAPS_API_KEY}`;
      const response = await fetch(url);
      const data = await response.json();

      if (data.status === "OK" && data.rows[0]?.elements[0]?.status === "OK") {
        const distanceValue = data.rows[0].elements[0].distance.value / 1000; // Convert to km
        const durationText = data.rows[0].elements[0].duration.text;
        setDistance({
          km: parseFloat(distanceValue.toFixed(2)),
          time: durationText,
        });
      }
    } catch (error) {
      console.error("Error calculating distance:", error);
    }
  }, []);

  // Get route polyline - will be handled by DirectionsService component
  const getRoute = useCallback((origin, destination) => {
    // Route will be calculated by DirectionsService component
    // This function is kept for consistency but actual routing is done in the component
  }, []);

  // Fetch tracking data
  const fetchTrackData = useCallback(async () => {
    try {
      const res = await fynitoApi.post("/order-track", { id: id });
      if (res.data.status_code === 200) {
        const data = res.data.data;
        setOrderDetails(data);
        setRestDetails(data.restaurant);

        const deliveryPartner = data.delivery_partner;
        const restaurant = data.restaurant;
        const deliveryAddress = data.delivery_address_detail;

        let currentLocation = null;
        let destination = null;

        // Determine current location and destination
        if (deliveryPartner && deliveryPartner.delivery_boy_lat && deliveryPartner.delivery_boy_long) {
          currentLocation = {
            lat: parseFloat(deliveryPartner.delivery_boy_lat),
            lng: parseFloat(deliveryPartner.delivery_boy_long),
          };
          
          if (deliveryPartner.pickedor_not === 1 && deliveryAddress) {
            destination = {
              lat: parseFloat(deliveryAddress.lat),
              lng: parseFloat(deliveryAddress.long),
            };
          } else if (restaurant) {
            destination = {
              lat: parseFloat(restaurant.latitude),
              lng: parseFloat(restaurant.longitude),
            };
          }
        } else if (restaurant && deliveryAddress) {
          currentLocation = {
            lat: parseFloat(restaurant.latitude),
            lng: parseFloat(restaurant.longitude),
          };
          destination = {
            lat: parseFloat(deliveryAddress.lat),
            lng: parseFloat(deliveryAddress.long),
          };
        }

        if (currentLocation && destination) {
          setMapPoints({ start: currentLocation, end: destination });
          
          // Update map center
          setMapCenter(currentLocation);
          
          // Update markers
          const updatedMarkers = updateMarkers(data);
          
          // Calculate distance if order not delivered
          if (data.status !== "Delivered") {
            calculateDistance(currentLocation, destination);
          }
        }
      } else {
        toastify.error(res.data.message);
      }
    } catch (error) {
      console.error("Error fetching tracking data:", error);
    }
  }, [id, updateMarkers, calculateDistance]);

  useEffect(() => {
    const pollIntervalMs = 10000;

    const runFetchIfVisible = () => {
      if (document.visibilityState !== "visible") return;
      fetchTrackData();
    };

    fetchTrackData();

    if (orderDetails.status && orderDetails.status !== "Delivered") {
      updateIntervalRef.current = setInterval(runFetchIfVisible, pollIntervalMs);
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") runFetchIfVisible();
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (updateIntervalRef.current) {
        clearInterval(updateIntervalRef.current);
        updateIntervalRef.current = null;
      }
    };
  }, [id, fetchTrackData, orderDetails.status]);

  // Handle Status Color
  const getStatusClass = (status) => {
    switch (status) {
      case "Pending":
        return "status-pending";
      case "Canceled":
        return "status-canceled";
      case "Delivered":
        return "status-delivered";
      default:
        return "status-info";
    }
  };

  // Handle navigation to support page
  const handleSupportClick = () => {
    navigate("/support", { state: orderDetails.order_id });
  };

  return (
    <>
      {/* Modern Header Section */}
      <section className="modern-track-header">
        <div className="container">
          <div className="modern-breadcrumb-wrapper">
            <nav aria-label="breadcrumb" className="modern-breadcrumb">
              <ol className="breadcrumb-list">
                <li className="breadcrumb-item">
                  <Link to="/" className="breadcrumb-link">
                    <i className="fas fa-home me-2"></i>
                    Home
                  </Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/" className="breadcrumb-link">
                    <i className="fas fa-shopping-bag me-2"></i>
                    Order
                  </Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  <i className="fas fa-truck me-2"></i>
                  Track Order
                </li>
              </ol>
            </nav>
            <div className="modern-title-section">
              <h1 className="modern-page-title">
                <i className="fas fa-route me-3"></i>
                Track Your Order
              </h1>
              <p className="modern-page-subtitle">
                Monitor your food delivery in real-time
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Progress Bar Section */}
      <section className="progress-section">
        <div className="container">
          <div className="progress-wrapper">
            <OrderProgressBar currentStatus={orderDetails.status} />
          </div>
        </div>
      </section>

      {/* Google Maps Section */}
      {mapPoints.start && mapPoints.end && (
        <section className="map-section">
          <div className="container">
            <div className="map-wrapper">
              <div className="map-header">
                <h5 className="map-title">
                  <i className="fas fa-map-marked-alt me-2"></i>
                  Live Delivery Tracking
                </h5>
                {distance.km > 0 && orderDetails.status !== "Delivered" && (
                  <div className="distance-info">
                    <span className="distance-item">
                      <i className="fas fa-route me-2"></i>
                      {distance.km} km
                    </span>
                    <span className="distance-item">
                      <i className="fas fa-clock me-2"></i>
                      {distance.time}
                    </span>
                  </div>
                )}
              </div>
              <div className="map-container">
                <LoadScriptNext
                  googleMapsApiKey={GOOGLE_MAPS_API_KEY}
                  libraries={libraries}
                >
                  <GoogleMap
                    mapContainerStyle={containerStyle}
                    center={mapCenter}
                    zoom={zoom}
                    options={{
                      zoomControl: false,
                      mapTypeControl: false,
                      streetViewControl: false,
                      fullscreenControl: true,
                      styles: [
                        {
                          featureType: "poi",
                          elementType: "labels",
                          stylers: [{ visibility: "off" }],
                        },
                      ],
                    }}
                    onLoad={(map) => {
                      mapRef.current = map;
                      if (window.google && window.google.maps) {
                        directionsServiceRef.current = new window.google.maps.DirectionsService();
                        
                        // Fit bounds to show all markers
                        if (markers.length > 0) {
                          const bounds = new window.google.maps.LatLngBounds();
                          markers.forEach((marker) => {
                            bounds.extend(marker.position);
                          });
                          map.fitBounds(bounds);
                        }
                      }
                    }}
                    onUnmount={() => {
                      mapRef.current = null;
                      directionsServiceRef.current = null;
                    }}
                  >
                    {/* Markers */}
                    {markers.map((marker) => (
                      <Marker
                        key={marker.id}
                        position={marker.position}
                        title={marker.title}
                        label={marker.label}
                        icon={marker.icon}
                      />
                    ))}

                    {/* Route Polyline */}
                    {polylinePath.length > 0 && (
                      <Polyline
                        path={polylinePath}
                        options={{
                          strokeColor: "#e42f49",
                          strokeWeight: 4,
                          strokeOpacity: 0.8,
                        }}
                      />
                    )}

                    {/* Directions Service - only render once per route to prevent infinite API calls */}
                    {(() => {
                      const currentMapPointsKey = mapPoints.start && mapPoints.end
                        ? `${mapPoints.start.lat},${mapPoints.start.lng}-${mapPoints.end.lat},${mapPoints.end.lng}`
                        : null;
                      const shouldRequestDirections = currentMapPointsKey
                        && directionsRequestedKeyRef.current !== currentMapPointsKey
                        && orderDetails.status !== "Delivered"
                        && window.google
                        && window.google.maps
                        && window.google.maps.TravelMode;
                      if (shouldRequestDirections) {
                        pendingDirectionsKeyRef.current = currentMapPointsKey;
                      }
                      return shouldRequestDirections ? (
                        <DirectionsService
                          options={{
                            destination: mapPoints.end,
                            origin: mapPoints.start,
                            travelMode: window.google.maps.TravelMode.DRIVING,
                          }}
                          callback={(result, status) => {
                            if (status === "OK" && result && result.routes && result.routes[0]) {
                              directionsRequestedKeyRef.current = pendingDirectionsKeyRef.current;
                              setDirectionsResponse(result);
                              const path = result.routes[0].overview_path.map((point) => ({
                                lat: point.lat(),
                                lng: point.lng(),
                              }));
                              setPolylinePath(path);
                            }
                          }}
                        />
                      ) : null;
                    })()}
                  </GoogleMap>
                </LoadScriptNext>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Main Content Section */}
      <section className="track-content-section">
        <div className="container">
          <div className="row g-4">
            {/* Restaurant & Order Info */}
            <div className="col-lg-8">
              <div className="modern-track-card restaurant-info-card">
                <div className="card-header-modern">
                  <h5 className="card-title-modern">
                    <i className="fas fa-store me-2"></i>
                    Restaurant Information
                  </h5>
                </div>
                <div className="card-body-modern">
                  <div className="restaurant-details">
                    <div className="restaurant-image-wrapper">
                      <img
                        src={restDetails.restaurant_image}
                        alt={restDetails.restaurant_name}
                        className="restaurant-image"
                      />
                    </div>
                    <div className="restaurant-info">
                      <h4 className="restaurant-name">{restDetails.restaurant_name}</h4>
                      <p className="restaurant-address">
                        <i className="fas fa-map-marker-alt me-2"></i>
                        {restDetails.full_address}
                      </p>
                    </div>
                  </div>
                  
                  {orderDetails.status !== "Delivered" && (
                    <div className="otp-section">
                      <div className="otp-card">
                        <div className="otp-header">
                          <h6 className="otp-title">
                            <i className="fas fa-key me-2"></i>
                            Delivery OTP
                          </h6>
                        </div>
                        <div className="otp-content">
                          <span className="otp-code">{orderDetails.otp}</span>
                          <div className="otp-actions">
                            <Link
                              to={`tel:+91${restDetails?.contact_number}`}
                              className="action-btn call-btn"
                              title="Call Restaurant"
                            >
                              <i className="fas fa-phone"></i>
                            </Link>
                            <button 
                              onClick={handleSupportClick}
                              className="action-btn support-btn"
                              title="Get Support"
                            >
                              <i className="fas fa-comment"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Order Status & Details */}
            <div className="col-lg-4">
              <div className="modern-track-card order-status-card">
                <div className="card-header-modern">
                  <h5 className="card-title-modern">
                    <i className="fas fa-info-circle me-2"></i>
                    Order Details
                  </h5>
                </div>
                <div className="card-body-modern">
                  <div className="status-badge">
                    <span className={`status-indicator ${getStatusClass(orderDetails.status)}`}>
                      <i className="fas fa-circle me-2"></i>
                      {orderDetails.status}
                    </span>
                  </div>
                  
                  <div className="order-details-list">
                    <div className="detail-item">
                      <span className="detail-label">
                        <i className="fas fa-hashtag me-2"></i>
                        Order ID
                      </span>
                      <span className="detail-value">{orderDetails.order_id}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">
                        <i className="fas fa-credit-card me-2"></i>
                        Payment
                      </span>
                      <span className="detail-value">{orderDetails.payment_method}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">
                        <i className="fas fa-calendar me-2"></i>
                        Order Date
                      </span>
                      <span className="detail-value">{orderDetails.order_date_time}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile Bottom Bar */}
      <MobileBottomBar />

      {/* Modern CSS Styles */}
      <style jsx>{`
        .modern-track-header {
          background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
          padding: 2rem 0 1.5rem;
          margin-top: 88px;
          border-bottom: 1px solid #e9ecef;
        }

        @media (max-width: 768px) {
          .modern-track-header {
            margin-top: 72px;
            padding: 1.5rem 0 1rem;
          }
        }

        .modern-breadcrumb-wrapper {
          max-width: 100%;
        }

        .modern-breadcrumb {
          margin-bottom: 1.5rem;
        }

        .breadcrumb-list {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 0.5rem;
          margin: 0;
          padding: 0;
          list-style: none;
        }

        .breadcrumb-item {
          display: flex;
          align-items: center;
        }

        .breadcrumb-link {
          color: #6c757d;
          text-decoration: none;
          padding: 0.5rem 0.75rem;
          border-radius: 8px;
          transition: all 0.3s ease;
          font-size: 0.9rem;
          font-weight: 500;
        }

        .breadcrumb-link:hover {
          color: #e42f49;
          background: rgba(228, 47, 73, 0.1);
          transform: translateY(-1px);
        }

        .breadcrumb-item.active {
          color: #e42f49;
          font-weight: 600;
        }

        .modern-title-section {
          text-align: left;
        }

        .modern-page-title {
          font-size: 2.5rem;
          font-weight: 700;
          color: #2f2e2e;
          margin: 0 0 0.5rem 0;
          background: linear-gradient(135deg, #e42f49 0%, #ff6b7a 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .modern-page-subtitle {
          font-size: 1.1rem;
          color: #6c757d;
          margin: 0;
          font-weight: 400;
        }

        @media (max-width: 768px) {
          .modern-page-title {
            font-size: 2rem;
          }
          .modern-page-subtitle {
            font-size: 1rem;
          }
        }

        .progress-section {
          background: #ffffff;
          padding: 1rem 0;
          border-bottom: 1px solid #e9ecef;
        }

        .progress-wrapper {
          max-width: 100%;
          overflow-x: auto;
          padding: 0 1rem;
        }

        .map-section {
          background: #f8f9fa;
          padding: 2rem 0;
          border-bottom: 1px solid #e9ecef;
        }

        .map-wrapper {
          background: #ffffff;
          border-radius: 16px;
          box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08);
          overflow: hidden;
          transition: all 0.3s ease;
        }

        .map-wrapper:hover {
          box-shadow: 0 12px 28px rgba(0, 0, 0, 0.12);
        }

        .map-header {
          background: linear-gradient(135deg, #e42f49 0%, #ff6b7a 100%);
          padding: 1.25rem 1.5rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .map-title {
          color: #ffffff;
          margin: 0;
          font-size: 1.1rem;
          font-weight: 600;
          display: flex;
          align-items: center;
        }

        .distance-info {
          display: flex;
          gap: 1.5rem;
          align-items: center;
        }

        .distance-item {
          color: #ffffff;
          font-size: 0.95rem;
          font-weight: 500;
          display: flex;
          align-items: center;
          background: rgba(255, 255, 255, 0.2);
          padding: 0.5rem 1rem;
          border-radius: 20px;
          backdrop-filter: blur(10px);
        }

        .map-container {
          width: 100%;
          height: 500px;
          position: relative;
        }

        @media (max-width: 768px) {
          .map-section {
            padding: 1.5rem 0;
          }

          .map-header {
            padding: 1rem;
            flex-direction: column;
            align-items: flex-start;
            gap: 0.75rem;
          }

          .map-title {
            font-size: 1rem;
          }

          .distance-info {
            width: 100%;
            justify-content: space-between;
            gap: 0.75rem;
          }

          .distance-item {
            font-size: 0.85rem;
            padding: 0.4rem 0.75rem;
            flex: 1;
            justify-content: center;
          }

          .map-container {
            height: 400px;
          }
        }

        @media (max-width: 480px) {
          .map-section {
            padding: 1rem 0;
          }

          .map-header {
            padding: 0.75rem;
          }

          .map-title {
            font-size: 0.9rem;
          }

          .distance-info {
            flex-direction: column;
            gap: 0.5rem;
          }

          .distance-item {
            width: 100%;
            font-size: 0.8rem;
          }

          .map-container {
            height: 350px;
          }
        }

        .track-content-section {
          background: #f8f9fa;
          padding: 2rem 0;
          min-height: 60vh;
        }

        .modern-track-card {
          background: #ffffff;
          border-radius: 16px;
          box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08);
          border: none;
          overflow: hidden;
          transition: all 0.3s ease;
          height: 100%;
        }

        .modern-track-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 12px 28px rgba(0, 0, 0, 0.12);
        }

        .card-header-modern {
          background: linear-gradient(135deg, #e42f49 0%, #ff6b7a 100%);
          padding: 1.25rem 1.5rem;
          border-bottom: none;
        }

        .card-title-modern {
          color: #ffffff;
          margin: 0;
          font-size: 1.1rem;
          font-weight: 600;
        }

        .card-body-modern {
          padding: 1.5rem;
        }

        .restaurant-details {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 1.5rem;
        }

        .restaurant-image-wrapper {
          flex-shrink: 0;
        }

        .restaurant-image {
          width: 80px;
          height: 80px;
          border-radius: 12px;
          object-fit: cover;
          border: 3px solid #f8f9fa;
        }

        .restaurant-info {
          flex: 1;
        }

        .restaurant-name {
          font-size: 1.3rem;
          font-weight: 600;
          color: #2f2e2e;
          margin: 0 0 0.5rem 0;
        }

        .restaurant-address {
          color: #6c757d;
          margin: 0;
          font-size: 0.95rem;
          line-height: 1.4;
        }

        .otp-section {
          margin-top: 1.5rem;
        }

        .otp-card {
          background: linear-gradient(135deg, #fff8f9 0%, #ffffff 100%);
          border: 2px solid #e42f49;
          border-radius: 12px;
          padding: 1.25rem;
        }

        .otp-header {
          margin-bottom: 1rem;
        }

        .otp-title {
          color: #e42f49;
          margin: 0;
          font-weight: 600;
          font-size: 1rem;
        }

        .otp-content {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
        }

        .otp-code {
          background: #e42f49;
          color: #ffffff;
          padding: 0.75rem 1rem;
          border-radius: 8px;
          font-size: 1.1rem;
          font-weight: 600;
          letter-spacing: 2px;
          min-width: 80px;
          text-align: center;
        }

        .otp-actions {
          display: flex;
          gap: 0.5rem;
        }

        .action-btn {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          border: none;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #ffffff;
          text-decoration: none;
          transition: all 0.3s ease;
          font-size: 1rem;
        }

        .call-btn {
          background: #28a745;
        }

        .call-btn:hover {
          background: #218838;
          transform: scale(1.1);
        }

        .support-btn {
          background: #17a2b8;
        }

        .support-btn:hover {
          background: #138496;
          transform: scale(1.1);
        }

        .status-badge {
          margin-bottom: 1.5rem;
          text-align: center;
        }

        .status-indicator {
          display: inline-flex;
          align-items: center;
          padding: 0.75rem 1.5rem;
          border-radius: 25px;
          font-weight: 600;
          font-size: 1rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .status-pending {
          background: linear-gradient(135deg, #ffc107 0%, #ffb300 100%);
          color: #ffffff;
        }

        .status-canceled {
          background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
          color: #ffffff;
        }

        .status-delivered {
          background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
          color: #ffffff;
        }

        .status-info {
          background: linear-gradient(135deg, #17a2b8 0%, #138496 100%);
          color: #ffffff;
        }

        .order-details-list {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .detail-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.75rem 0;
          border-bottom: 1px solid #f1f3f4;
        }

        .detail-item:last-child {
          border-bottom: none;
        }

        .detail-label {
          color: #6c757d;
          font-weight: 500;
          font-size: 0.9rem;
        }

        .detail-value {
          color: #2f2e2e;
          font-weight: 600;
          font-size: 0.9rem;
          text-align: right;
        }

        @media (max-width: 768px) {
          .restaurant-details {
            flex-direction: column;
            text-align: center;
          }

          .otp-content {
            flex-direction: column;
            text-align: center;
          }

          .otp-actions {
            justify-content: center;
          }

          .detail-item {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.25rem;
          }

          .detail-value {
            text-align: left;
          }
        }
      `}</style>
    </>
  );
};

export default TrackOrder;
